import {
  useEffect,
  useState,
} from "react";
import {
  apiRequest,
} from "../../shared/api/client";

type TeamMember = {
  id: string;
  email: string;
  name: string;
  initials: string;
  title: string;
  department: string;
  role: string;
  roleCode: string;
  roles: string[];
  access: string;
  status: string;
};

type DirectoryUser = {
  id: string;
  name: string;
  email: string;
  title: string;
  department: string;
};

export type TeamModuleProps = {
  notify: (message: string) => void;
  dataRefreshVersion: number;
};

export function useTeamWorkspace({
  notify,
  dataRefreshVersion,
}: TeamModuleProps) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [directoryQuery, setDirectoryQuery] = useState("");
  const [directoryUsers, setDirectoryUsers] = useState<DirectoryUser[]>([]);
  const [selectedDirectoryUserId, setSelectedDirectoryUserId] = useState("");
  const [directorySearching, setDirectorySearching] = useState(false);
  const [directorySearchFailed, setDirectorySearchFailed] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    title: "",
    department: "",
    status: "ACTIVE",
    roles: [] as string[],
  });
  const [form, setForm] = useState({
    email: "",
    name: "",
    department: "Planlama",
    role: "PLANLAMA",
  });
  useEffect(() => {
    apiRequest<TeamMember[]>("/api/team")
      .then(setMembers)
      .catch(() => notify("Ekip bilgileri sunucudan alınamadı."));
  }, [dataRefreshVersion, notify]);
  useEffect(() => {
    const query = directoryQuery.trim();
    if (query.length < 2) {
      setDirectoryUsers([]);
      setDirectorySearching(false);
      setDirectorySearchFailed(false);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setDirectorySearching(true);
      setDirectorySearchFailed(false);
      apiRequest<DirectoryUser[]>(
        `/api/team/directory-search?q=${encodeURIComponent(query)}`,
        { signal: controller.signal },
      )
        .then(setDirectoryUsers)
        .catch((error) => {
          if (error instanceof DOMException && error.name === "AbortError")
            return;
          setDirectoryUsers([]);
          setDirectorySearchFailed(true);
        })
        .finally(() => setDirectorySearching(false));
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [directoryQuery]);
  const selectDirectoryUser = (userId: string) => {
    setSelectedDirectoryUserId(userId);
    const user = directoryUsers.find((candidate) => candidate.id === userId);
    if (!user) return;
    const supportedDepartments = [
      "Planlama",
      "Üretim",
      "Lojistik",
      "Teknik",
      "Satın Alma",
      "Bilgi Teknolojileri",
      "Genel Müdürlük",
    ];
    setForm((current) => ({
      ...current,
      name: user.name,
      email: user.email,
      department: supportedDepartments.includes(user.department)
        ? user.department
        : current.department,
    }));
  };
  const inviteMember = async () => {
    if (!form.email.trim() || !form.name.trim()) {
      notify("Ad soyad ve kurumsal e-posta zorunludur.");
      return;
    }
    if (
      members.some(
        (member) =>
          member.email.toLocaleLowerCase("tr") ===
          form.email.trim().toLocaleLowerCase("tr"),
      )
    ) {
      notify("Bu e-posta adresi için daha önce erişim tanımlanmış.");
      return;
    }
    setInviteSubmitting(true);
    try {
      const member = await apiRequest<TeamMember>("/api/team", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setMembers((current) => [...current, member]);
      setForm({
        email: "",
        name: "",
        department: "Planlama",
        role: "PLANLAMA",
      });
      setDirectoryQuery("");
      setDirectoryUsers([]);
      setSelectedDirectoryUserId("");
      setComposerOpen(false);
      notify(`${member.name} için davet e-postası kuyruğa alındı.`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Davet oluşturulamadı.");
    } finally {
      setInviteSubmitting(false);
    }
  };
  const openEditor = (member: TeamMember) => {
    setEditing(member);
    setEditForm({
      name: member.name,
      title: member.title,
      department: member.department,
      status:
        member.status === "Aktif"
          ? "ACTIVE"
          : member.status === "Pasif"
            ? "PASSIVE"
            : "PENDING",
      roles: member.roles,
    });
  };
  const toggleRole = (role: string) => {
    setEditForm((current) => ({
      ...current,
      roles: current.roles.includes(role)
        ? current.roles.filter((item) => item !== role)
        : [...current.roles, role],
    }));
  };
  const saveMember = async () => {
    if (!editing || !editForm.name.trim() || !editForm.roles.length) {
      notify("Kullanıcı adı ve en az bir rol zorunludur.");
      return;
    }
    try {
      const updated = await apiRequest<TeamMember>(`/api/team/${editing.id}`, {
        method: "PUT",
        body: JSON.stringify(editForm),
      });
      setMembers((current) =>
        current.map((member) => (member.id === updated.id ? updated : member)),
      );
      setEditing(null);
      notify(`${updated.name} kullanıcısının erişimi güncellendi.`);
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Kullanıcı güncellenemedi.",
      );
    }
  };
  return {
    members,
    composerOpen,
    setComposerOpen,
    inviteSubmitting,
    directoryQuery,
    setDirectoryQuery,
    directoryUsers,
    selectedDirectoryUserId,
    directorySearching,
    directorySearchFailed,
    editing,
    setEditing,
    editForm,
    setEditForm,
    form,
    setForm,
    selectDirectoryUser,
    inviteMember,
    openEditor,
    toggleRole,
    saveMember,
  };
}
